import React, { useState, useEffect } from 'react';
import { Badge } from 'reactstrap';
import {
    Alert,
    IconButton,
    Tooltip,
    Box,
    Chip,
    Collapse,
    CircularProgress
} from '@mui/material';
import { DocumentPreviewModal } from '../../DocumentPreviewModal/DocumentPreviewModal';
import {
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    ArrowRightAlt as ArrowRightAltIcon,
} from '@mui/icons-material';
import { del, getFullUrl } from '../../../components/hooks/apiFetch';
import {
    summarizeResumeIcon,
    darkSummarizeResumeIcon,
    retellingIcon,
    darkRetellingIcon
} from '../../../assets/icons';
import { getFileIcon } from '../../Utils/FileIcons';
import { useUserNotifications } from '../shared/hooks/useUserNotifications';
import { useTheme } from '../../../components/hooks/useTheme';
import './FileTable.css';

export const FileTable = ({
    id,
    records,
    inputIdentifier,
    currentUserAccess,
    onFileUpdated,
    onSendToSummarizeResume,
    onSendToRetelling
}) => {
    const downloadFileUrl = getFullUrl(`/api/OperationInfo/File/${encodeURIComponent(inputIdentifier)}`);
    const { theme } = useTheme();
    const { sendFileVersionNotifications } = useUserNotifications();
    const [deletingIndex, setDeletingIndex] = useState(null);
    const [sendingToSummarizeResumeIndex, setSendingToSummarizeResumeIndex] = useState(null);
    const [sendingToRetellingIndex, setSendingToRetellingIndex] = useState(null);
    const [alertInfo, setAlertInfo] = useState({ show: false, message: '', severity: 'error' });
    const [expandedFiles, setExpandedFiles] = useState(new Set());

    const getIcon = (lightIcon, darkIcon) => theme === 'light' ? lightIcon : darkIcon;

    useEffect(() => {
        if (records && records.length > 0) {
            const filesWithTrees = groupRecordsByOriginalFile();
            const allOriginalFiles = filesWithTrees.map(file => file.originalFile);
            setExpandedFiles(new Set(allOriginalFiles));
        }
    }, [records]);

    // Функция для рендеринга имени файла с иконкой
    const renderFileNameWithIcon = (fileName, version = null, downloadUrl = null, className = "") => {
        const fileContent = (
            <div className={`file-name-with-icon ${className}`}>
                {getFileIcon(fileName, theme)}
                <span className="file-name-text ml-1">
                    {fileName} {version && `(v.${version})`}
                </span>
            </div>
        );

        if (downloadUrl) {
            return (
                <a href={downloadUrl} download className="file-link">
                    {fileContent}
                </a>
            );
        }

        return fileContent;
    };

    if (!records || records.length === 0) return null;

    // Группируем записи по оригинальным файлам
    const groupRecordsByOriginalFile = () => {
        const filesMap = new Map();

        records.forEach(record => {
            const key = record.originalFileName || record.baseFileId;
            if (!filesMap.has(key)) {
                filesMap.set(key, []);
            }
            filesMap.get(key).push(record);
        });

        // Для каждого файла строим древовидную структуру версий
        const filesWithTrees = [];

        filesMap.forEach((fileRecords, originalFile) => {
            // Сортируем по версии
            fileRecords.sort((a, b) => a.version - b.version);

            // Находим первую версию
            const firstVersion = fileRecords.find(record => record.version === 1);

            // Остальные версии (кроме первой)
            const otherVersions = fileRecords.filter(record => record.version > 1);

            filesWithTrees.push({
                originalFile,
                firstVersion,
                otherVersions,
                records: fileRecords,
                latestVersion: Math.max(...fileRecords.map(r => r.version))
            });
        });

        return filesWithTrees;
    };

    const filesWithTrees = groupRecordsByOriginalFile();

    const getParentVersionRecord = (currentRecord, fileInfo) => {
        if (!currentRecord.oldVersion) return null;

        return fileInfo.records.find(record => record.version === Number(currentRecord.oldVersion));
    };


    const getOutputFileDownloadUrl = (record) => {
        const fileName = encodeURIComponent(record.fileName);
        const version = encodeURIComponent(record.filePath?.match(/v\.(\d+)/)?.[1] || '1');
        const baseFileId = encodeURIComponent(record.baseFileId);

        const url = `${downloadFileUrl}?fileName=${fileName}&version=${version}&baseFileId=${baseFileId}`;
        return url;
    };

    // Функция для получения URL скачивания родительской версии
    const getParentVersionDownloadUrl = (currentRecord, fileInfo) => {
        const parentRecord = getParentVersionRecord(currentRecord, fileInfo);
        if (!parentRecord || !parentRecord.isSuccess) return null;
        return getOutputFileDownloadUrl(parentRecord);
    };

    // Функция для получения имени файла родительской версии
    const getParentVersionFileName = (currentRecord, fileInfo) => {
        const parentRecord = getParentVersionRecord(currentRecord, fileInfo);
        return parentRecord ? parentRecord.fileName : null;
    };

    const canDelete = (record) => {
        return record.canDeleteAllVersions || (record.canDeleteOwnVersions && record.userName === record.changeByUserName);
    };

    // Вычисляем общее количество колонок
    const getTotalColumnsCount = () => {
        let count = 1; // expand column
        if (records.some(x => x.isNew)) count++;
        count += 7; // version, status, original file, output file, author, change time, actions
        return count;
    };

    const totalColumns = getTotalColumnsCount();

    // Получаем ширины колонок для выравнивания
    const getColumnWidths = () => {
        const widths = {
            expand: '50px',
            new: '60px',
            version: '150px',
            status: '60px',
            originalFile: '350px',
            outputFile: '350px',
            author: '200px',
            changeTime: '200px',
            actions: '200px'
        };
        return widths;
    };

    const columnWidths = getColumnWidths();

    const toggleFileExpansion = (originalFile) => {
        const newExpanded = new Set(expandedFiles);
        if (newExpanded.has(originalFile)) {
            newExpanded.delete(originalFile);
        } else {
            newExpanded.add(originalFile);
        }
        setExpandedFiles(newExpanded);
    };

    const showAlert = (message, severity = 'error') => {
        setAlertInfo({ show: true, message, severity });
        setTimeout(() => {
            setAlertInfo({ show: false, message: '', severity: 'error' });
        }, 5000);
    };

    const handleDelete = async (record, index, fileVersion) => {
        if (fileVersion <= 1) {
            showAlert('Нельзя удалять первую версию файла', 'warning');
            return;
        }

        if (window.confirm('Вы уверены, что хотите удалить эту версию файла?')) {
            setDeletingIndex(index);
            const fileName = encodeURIComponent(record.fileName);
            const version = encodeURIComponent(record.filePath?.match(/v\.(\d+)/)?.[1] || '1');
            const baseFileId = encodeURIComponent(record.baseFileId);
            try {
                await del(`/api/FileChanges/DeleteVersion/${inputIdentifier}?fileName=${fileName}&version=${version}&baseFileId=${baseFileId}`);
                await sendFileVersionNotifications('delete', record, currentUserAccess?.user, id);

                if (onFileUpdated) {
                    await onFileUpdated('delete');
                }
            } catch (error) {
                console.error('Ошибка удаления:', error);
                showAlert('Не удалось удалить версию', 'error');
            } finally {
                setDeletingIndex(null);
            }
        }
    };

    const closeAlert = () => {
        setAlertInfo({ show: false, message: '', severity: 'error' });
    };

    const getBadgeClass = (version, isLatest = false) => {
        if (version === 1) return 'first-version';
        if (isLatest) return 'last-version';
        return 'version';
    };

    const renderVersionBadge = (version, isLatest = false, isFirst = false, prevVersion) => {
        if (isFirst) {
            return (
                <Chip
                    label={`v${version}`}
                    size="small"
                    className={`version-badge ${getBadgeClass(version)}`}
                />
            );
        } else {
            return (
                <>
                    <Chip
                        label={`v${prevVersion}`}
                        size="small"
                        className={`version-badge ${getBadgeClass(prevVersion)}`}
                    />
                    <ArrowRightAltIcon size="small" />
                    <Chip
                        label={`v${version}`}
                        size="small"
                        className={`version-badge ${getBadgeClass(version, isLatest)}`}
                    />
                </>
            );
        }

    };

    // Обработчики кликов с управлением состоянием загрузки
    const handleSendToSummarize = async (record, downloadOutputFileUrl, index) => {
        if (!onSendToSummarizeResume) return;
        setSendingToSummarizeResumeIndex(index);
        try {
            await onSendToSummarizeResume(record, downloadOutputFileUrl);
        } finally {
            setSendingToSummarizeResumeIndex(null);
        }
    };

    const handleSendToRetelling = async (record, downloadOutputFileUrl, index) => {
        if (!onSendToRetelling) return;
        setSendingToRetellingIndex(index);
        try {
            await onSendToRetelling(record, downloadOutputFileUrl);
        } finally {
            setSendingToRetellingIndex(null);
        }
    };

    const renderVersionRow = (record, fileInfo, isFirstVersion = false) => {
        const isLatest = record.version === fileInfo.latestVersion;
        const origianlFileName = encodeURIComponent(record.originalFileName);
        const baseFileId = encodeURIComponent(record.baseFileId);
        const downloadInputFileUrl = `${downloadFileUrl}?fileName=${origianlFileName}&baseFileId=${baseFileId}&isOriginalFile=${encodeURIComponent(true)}`;
        const downloadOutputFileUrl = getOutputFileDownloadUrl(record);
        const parentVersionDownloadUrl = getParentVersionDownloadUrl(record, fileInfo);
        const parentVersionFileName = getParentVersionFileName(record, fileInfo);

        const rowIndex = `${fileInfo.originalFile}-v${record.version}`;
        const isExpanded = expandedFiles.has(fileInfo.originalFile);
        const hasOtherVersions = fileInfo.otherVersions.length > 0;

        if (!record.canRead) return null;

        return (
            <tr key={rowIndex} className={`version-row ${isFirstVersion ? 'first-version' : 'other-version'}`}>
                <td className="text-center align-middle" style={{ width: columnWidths.expand }}>
                    {isFirstVersion && hasOtherVersions && (
                        <Tooltip title={isExpanded ? "Скрыть остальные версии" : "Показать все версии"}>
                            <IconButton
                                size="small"
                                onClick={() => toggleFileExpansion(fileInfo.originalFile)}
                                className="expand-button"
                            >
                                {isExpanded
                                    ? <ExpandLessIcon className="search-icon" />
                                    : <ExpandMoreIcon className="search-icon" />
                                }
                            </IconButton>
                        </Tooltip>
                    )}
                </td>

                {records.some(x => x.isNew) && (
                    <td className="text-center align-middle" style={{ width: columnWidths.new }}>
                        {record.isNew && (
                            <Badge color="primary" pill className="unread-badge">
                                Новое
                            </Badge>
                        )}
                    </td>
                )}

                <td className="text-center align-middle version-indicator" style={{ width: columnWidths.version }}>
                    {renderVersionBadge(record.version, isLatest, isFirstVersion, record.oldVersion)}
                </td>

                <td className="text-center align-middle" style={{ width: columnWidths.status }}>
                    <Tooltip title={record.isSuccess ? 'Успешно выполнено' : 'Ошибка выполнения'}>
                        {record.isSuccess ? (
                            <span className="status-success">✓</span>
                        ) : (
                            <span className="status-error">✕</span>
                        )}
                    </Tooltip>
                </td>

                <td className="text-center align-middle" style={{ width: columnWidths.originalFile }}>
                    <Tooltip title={isFirstVersion ? fileInfo.originalFile : (`${parentVersionFileName} (v.${record.oldVersion})` || "Родительская версия недоступна")}>
                        <div className="file-name-container">
                            {isFirstVersion ? (
                                // Для первой версии показываем оригинальный файл
                                renderFileNameWithIcon(fileInfo.originalFile, null, downloadInputFileUrl)
                            ) : (
                                // Для последующих версий показываем файл результата родительской версии
                                parentVersionDownloadUrl && parentVersionFileName ? (
                                    renderFileNameWithIcon(
                                        parentVersionFileName,
                                        record.oldVersion,
                                        parentVersionDownloadUrl
                                    )
                                ) : (
                                    <div className="file-name-with-icon unavailable">
                                        {getFileIcon(parentVersionFileName, record.oldVersion)}
                                        <span className="file-name-text ml-1 original-file-name">
                                            {`${parentVersionFileName} (v.${record.oldVersion})` || "Недоступно"}
                                        </span>
                                    </div>
                                )
                            )}
                        </div>
                    </Tooltip>
                </td>

                <td className="text-center align-middle" style={{ width: columnWidths.outputFile }}>
                    <Tooltip title={`${record.fileName} (v.${record.version})`}>
                        <div className="file-name-container">
                            {record.isSuccess && record.canRead ? (
                                renderFileNameWithIcon(
                                    record.fileName,
                                    record.version,
                                    downloadOutputFileUrl
                                )
                            ) : (
                                <div className="file-name-with-icon">
                                    {getFileIcon(record.fileName, record.version)}
                                    <span className="file-name-text ml-1">
                                        {record.fileName} (v.{record.version})
                                    </span>
                                </div>
                            )}
                        </div>
                    </Tooltip>
                </td>

                <td className="text-center align-middle" style={{ width: columnWidths.creator }}>
                    {isFirstVersion ? (
                        <span>
                            {record.creator}
                        </span>
                    ) : (
                        (record.changeBy)
                            ? record.changeBy
                            : '—'
                    )}
                </td>

                <td className="text-center align-middle" style={{ width: columnWidths.changeTime }}>
                    {record.changeTime ? (
                        <span>
                            {new Date(record.changeTime).toLocaleString()}
                        </span>
                    ) : '-'}
                </td>

                <td className="text-center align-middle action-td" style={{ width: columnWidths.actions }}>
                    <div className="action-buttons-container">
                        {record.canRead && record.isSuccess && (
                            <>
                                <DocumentPreviewModal
                                    id={id}
                                    inputIdentifier={inputIdentifier}
                                    fileName={record.fileName}
                                    version={record.version || '1'}
                                    baseFileId={record.baseFileId}
                                    downloadUrl={downloadOutputFileUrl}
                                    onFileUpdated={onFileUpdated}
                                    record={record}
                                    canEdit={record.canEdit}
                                    currentUserAccess={currentUserAccess}
                                    sendFileVersionNotifications={sendFileVersionNotifications}
                                />


                                {onSendToSummarizeResume && (
                                    <Tooltip title="Отправить на резюмирование протокола совещания">
                                        <IconButton
                                            onClick={() => handleSendToSummarize(record, downloadOutputFileUrl, rowIndex)}
                                            size="small"
                                            disabled={sendingToSummarizeResumeIndex === rowIndex || sendingToRetellingIndex === rowIndex}
                                            className="icon-button active-btn"
                                        >
                                            {sendingToSummarizeResumeIndex === rowIndex ? (
                                                <CircularProgress size={20} />
                                            ) : (
                                                <img
                                                    className="active-btn"
                                                    src={getIcon(summarizeResumeIcon, darkSummarizeResumeIcon)}
                                                    alt="Резюмирование"
                                                    style={{ width: 25, height: 25 }}
                                                />
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {onSendToRetelling && (
                                    < Tooltip title="Отправить на адаптивное резюмирование и пересказ текста">
                                        <IconButton
                                            onClick={() => handleSendToRetelling(record, downloadOutputFileUrl, rowIndex)}
                                            size="small"
                                            disabled={sendingToSummarizeResumeIndex === rowIndex || sendingToRetellingIndex === rowIndex}
                                            className="icon-button active-btn"
                                        >
                                            {sendingToRetellingIndex === rowIndex ? (
                                                <CircularProgress size={20} />
                                            ) : (
                                                <img
                                                    className="active-btn"
                                                    src={getIcon(retellingIcon, darkRetellingIcon)}
                                                    alt="Пересказ"
                                                    style={{ width: 20, height: 20 }}
                                                />
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                )}


                                {records.some(x => canDelete(x)) && (
                                    <Tooltip title={record.version <= 1 ? "Нельзя удалить первую версию" : "Удалить версию"}>
                                        <span>
                                            <IconButton
                                                onClick={() => handleDelete(record, `${fileInfo.originalFile}-v${record.version}`, record.version)}
                                                size="small"
                                                disabled={!canDelete(record) || record.version <= 1 || deletingIndex !== null}
                                                className="icon-button"
                                            >
                                                {deletingIndex === `${fileInfo.originalFile}-v${record.version}` ? (
                                                    <span className="material-symbols-outlined icon delete-icon spinning">
                                                        auto_delete
                                                    </span>
                                                ) : (
                                                    <span className="material-symbols-outlined icon delete-icon">
                                                        delete
                                                    </span>
                                                )}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                            </>
                        )}
                    </div>
                </td>
            </tr >
        );
    };

    const VersionLegend = () => (
        <Box className="version-legend" sx={{ mb: 2, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <div className="legend-title">Обозначения версий:</div>
            <div className="legend-items">
                <div className="legend-item">
                    <Chip label="v1" className="version-badge first-version" size="small" />
                    <span> — Первая версия файла</span>
                </div>
                <div className="legend-item">
                    <Chip label="vX" className="version-badge last-version" size="small" />
                    <span> — Последняя версия файла</span>
                </div>
                <div className="legend-item">
                    <Chip label="vX" className="version-badge version" size="small" />
                    <span> — Промежуточные версии</span>
                </div>
            </div>
        </Box>
    );

    return (
        <div className="file-table-wrapper">
            {alertInfo.show && (
                <Alert
                    severity={alertInfo.severity}
                    onClose={closeAlert}
                    style={{ marginBottom: '16px' }}
                >
                    {alertInfo.message}
                </Alert>
            )}

            <VersionLegend />

            {records.some(x => x.canRead) ? (
                <div className="table-scroll-container">
                    <div className="unified-table-container">
                        <table className="unified-file-table">
                            <thead>
                                <tr>
                                    <th className="text-center align-middle" style={{ width: columnWidths.expand }}></th>
                                    {records.some(x => x.isNew) && (
                                        <th className="text-center align-middle" style={{ width: columnWidths.new }}></th>
                                    )}
                                    <th className="text-center align-middle" style={{ width: columnWidths.version }}>Версия</th>
                                    <th className="text-center align-middle" style={{ width: columnWidths.status }}>Статус</th>
                                    <th className="text-center align-middle" style={{ width: columnWidths.originalFile }}>Исходный Файл</th>
                                    <th className="text-center align-middle" style={{ width: columnWidths.outputFile }}>Результат</th>
                                    <th className="text-center align-middle" style={{ width: columnWidths.author }}>Автор</th>
                                    <th className="text-center align-middle" style={{ width: columnWidths.changeTime }}>Изменён</th>
                                    <th className="text-center align-middle" style={{ width: columnWidths.actions }}>Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filesWithTrees.map((fileInfo, index) => {
                                    const isExpanded = expandedFiles.has(fileInfo.originalFile);
                                    const hasOtherVersions = fileInfo.otherVersions.length > 0;

                                    return (
                                        <React.Fragment key={fileInfo.originalFile}>
                                            {/* Первая версия файла */}
                                            {fileInfo.firstVersion && renderVersionRow(fileInfo.firstVersion, fileInfo, true)}

                                            {/* Остальные версии с анимацией раскрытия */}
                                            {hasOtherVersions && (
                                                <tr className="version-collapse-row">
                                                    <td colSpan={totalColumns} className="p-0">
                                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                            <div className="nested-versions">
                                                                {/* Вложенная таблица с такими же колонками */}
                                                                <table className="nested-table" style={{ width: '100%' }}>
                                                                    <colgroup>
                                                                        <col style={{ width: columnWidths.expand }} />
                                                                        {records.some(x => x.isNew) && <col style={{ width: columnWidths.new }} />}
                                                                        <col style={{ width: columnWidths.version }} />
                                                                        <col style={{ width: columnWidths.status }} />
                                                                        <col style={{ width: columnWidths.originalFile }} />
                                                                        <col style={{ width: columnWidths.outputFile }} />
                                                                        <col style={{ width: columnWidths.author }} />
                                                                        <col style={{ width: columnWidths.changeTime }} />
                                                                        <col style={{ width: columnWidths.actions }} />
                                                                    </colgroup>
                                                                    <tbody>
                                                                        {fileInfo.otherVersions.map(record =>
                                                                            renderVersionRow(record, fileInfo, false)
                                                                        )}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </Collapse>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <p className="text-center mt-5">Нет доступных для просмотра файлов</p>
            )}
        </div>
    );
};